import { environment } from '../../environments/environment';

/** Site identifier of the only partner allowed to run the sso authorize flow. */
export const DILLARDS_SITE_ID = 'PLCN_DILLARDS';

/**
 * Path used whenever a partner return URL cannot be trusted: the browser stays on Citi and
 * lands on the standard Dillards signon page.
 */
export const DILLARDS_SIGNON_FALLBACK_PATH = '/RSauth/signon?pageName=signon&siteId=PLCN_DILLARDS&langId=en_US';

function getConfiguredDillardsHosts(allowedHostsOverride?: string[]): string[] {
  if (Array.isArray(allowedHostsOverride)) {
    return allowedHostsOverride;
  }
  const hosts = (environment as { dillardsPartnerRedirectHosts?: string[] }).dillardsPartnerRedirectHosts;
  return Array.isArray(hosts) ? hosts : [];
}

/**
 * Returns true when `value` carries script or markup characters, checking both the raw and the
 * percent-decoded form against the configured cross site scripting pattern. A local copy of that
 * pattern is used because the configured one is global and `test()` would advance `lastIndex`.
 */
function containsScriptMarkup(value: string): boolean {
  const configured = (environment as { crossSiteScriptingRegex?: RegExp }).crossSiteScriptingRegex;
  if (!(configured instanceof RegExp)) {
    return false;
  }
  const pattern = new RegExp(configured.source, configured.flags.replace('g', ''));
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return true;
  }
  return pattern.test(value) || pattern.test(decoded);
}

/**
 * Returns true only when `url` is an absolute https URL whose host matches one of the Dillards
 * return hosts configured for this environment. Hosts are compared in full, so look-alike domains
 * such as `evil-dillards.com` or `dillards.com.attacker.com` are rejected.
 *
 * `allowedHostsOverride` lets callers supply the hosts from the runtime app config (selected per
 * environment by the server), since the statically bundled `environment` is always the prod build.
 */
export function isSafeDillardsPartnerRedirectUrl(url: string | null | undefined, allowedHostsOverride?: string[]): url is string {
  if (typeof url !== 'string' || url.trim() === '') {
    return false;
  }
  const candidate = url.trim();
  if (containsScriptMarkup(candidate)) {
    return false;
  }
  const allowedHosts = getConfiguredDillardsHosts(allowedHostsOverride);
  if (allowedHosts.length === 0) {
    return false;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'https:' || !parsed.hostname) {
      return false;
    }
    const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');
    return allowedHosts.some(host => typeof host === 'string' && host.trim().toLowerCase() === hostname);
  } catch {
    return false;
  }
}
